import { Info, Lock, MessageSquareText, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { stageText } from "@/lib/contacts";
import { DELETED_NOTE, NOT_YOURS, SAME_PHONE_CAVEAT, initials, siblingHistory } from "@/lib/same-phone";
import type { LeadSibling } from "@/lib/types";
import { cn, formatDateTime, relativeTime } from "@/lib/utils";

/**
 * The other records with a lead's phone (ACL-336, ADR-115 in the platform repo), each on its own:
 * its firm, stage and date as the record always listed them, and what happened to it (the last
 * outcome, a firm's reason for turning it down, the newest note) when the reader may open it. In
 * the record's Same phone card, the opportunity's Associated tab, and `compact` on the phone.
 *
 * It never says two records are the same person: they share a number, and the caveat says so.
 */
export function SamePhoneRecords({
  siblings,
  phone,
  limit,
  compact = false,
  caveat = true,
  footer,
  label = "Other records with this phone",
}: {
  siblings: LeadSibling[];
  phone: string;
  /** Show at most this many, then a link to the contacts list searched by the phone. */
  limit?: number;
  compact?: boolean;
  /** Off where the caveat is said already, as in the record's card header. */
  caveat?: boolean;
  /** Drawn under each record: how the record and this one relate, where somebody said (ACL-337). */
  footer?: (sibling: LeadSibling) => ReactNode;
  /** The list's name, for records listed by something other than the phone (ACL-409's panel). */
  label?: string;
}) {
  if (siblings.length === 0) return null;
  const shown = limit === undefined ? siblings : siblings.slice(0, limit);
  const more = siblings.length - shown.length;

  return (
    <div className="min-w-0">
      {caveat && <SamePhoneCaveat compact={compact} />}
      <ul className={cn("divide-y divide-border", caveat && "mt-1.5")} aria-label={label}>
        {shown.map((s) => (
          <SamePhoneRecord key={s.id} sibling={s} compact={compact} footer={footer?.(s)} />
        ))}
      </ul>
      {more > 0 && (
        <Link
          to={`/contacts?q=${encodeURIComponent(phone)}`}
          className="mb-2 inline-flex text-xs font-medium text-accent-11 hover:underline"
        >
          View all {siblings.length} records
        </Link>
      )}
    </div>
  );
}

/** They share a number, and nothing more is known. */
export function SamePhoneCaveat({ compact = false, className }: { compact?: boolean; className?: string }) {
  return (
    <p className={cn("flex items-start gap-1.5 text-text-muted", compact ? "text-2xs" : "text-xs", className)}>
      <Info className={cn("shrink-0", compact ? "mt-px size-3" : "mt-0.5 size-3.5")} aria-hidden />
      <span>{SAME_PHONE_CAVEAT}</span>
    </p>
  );
}

function SamePhoneRecord({
  sibling,
  compact,
  footer,
}: {
  sibling: LeadSibling;
  compact: boolean;
  footer?: ReactNode;
}) {
  const history = siblingHistory(sibling);
  const locked = !history.mayOpen;
  const stage = stageText(sibling.stage);
  const firm = sibling.firm?.label ?? "No firm";
  // An older core-api sends no `may_open` and no name: the record is named by its firm, as before.
  const title = history.deleted
    ? "Deleted record"
    : (history.name ?? (sibling.may_open === undefined ? firm : locked ? "Restricted record" : "No name"));
  const added = formatDateTime(sibling.created_at).split(",").slice(0, 2).join(",");
  const facts: { term: string; value: string; tone?: "warning" }[] = [
    ...(history.outcome ? [{ term: "Outcome", value: history.outcome }] : []),
    ...(history.reason ? [{ term: "Firm's reason", value: history.reason, tone: "warning" as const }] : []),
  ];

  return (
    <li className={cn("flex gap-3", compact ? "py-2" : "py-3")}>
      <span
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full font-semibold",
          compact ? "size-6 text-2xs" : "size-8 text-xs",
          locked ? "bg-gray-3 text-gray-9" : "bg-accent-3 text-accent-11",
        )}
        aria-hidden
      >
        {history.deleted ? (
          <Trash2 className={compact ? "size-3" : "size-3.5"} />
        ) : locked ? (
          <Lock className={compact ? "size-3" : "size-3.5"} />
        ) : (
          (initials(history.name) ?? "?")
        )}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {locked ? (
              <p className={cn("truncate font-medium text-text-muted", compact ? "text-xs" : "text-sm")}>{title}</p>
            ) : (
              <Link
                to={`/contacts/${sibling.id}`}
                className={cn(
                  "block truncate font-semibold text-text hover:text-accent-11 hover:underline",
                  compact ? "text-xs" : "text-sm",
                )}
              >
                {title}
              </Link>
            )}
            <p className="truncate text-xs text-text-muted" title={firm}>
              {firm}
            </p>
            {(sibling.same_person !== null && sibling.same_person !== undefined) || sibling.accident_number ? (
              <p className="truncate text-2xs text-text-subtle">
                {[
                  sibling.same_person === true ? "This person" : sibling.same_person === false ? "Someone else" : null,
                  sibling.accident_number,
                ].filter(Boolean).join(" · ")}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-0.5">
            <span className="flex items-center gap-1">
              {sibling.dnc && sibling.stage !== "dnc" && (
                <Badge tone="danger" size="sm">
                  DNC
                </Badge>
              )}
              <Badge tone={stage.tone} size="sm" title={stage.description}>
                {stage.label}
              </Badge>
            </span>
            <span className="text-2xs text-text-subtle" title="When this record came in">
              {added}
            </span>
          </div>
        </div>

        {facts.length > 0 && (
          <dl
            className={cn(
              "mt-2 grid gap-x-3 gap-y-1 text-xs",
              compact ? "grid-cols-[5.25rem_1fr]" : "grid-cols-[6rem_1fr]",
            )}
          >
            {facts.map((f) => (
              <div key={f.term} className="contents">
                <dt className="text-text-muted">{f.term}</dt>
                <dd className={cn("min-w-0 break-words", f.tone === "warning" ? "font-medium text-warning-11" : "text-text")}>
                  {f.value}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {history.note && (
          <div className="mt-2 rounded-md border border-border bg-surface-sunken px-2.5 py-2">
            <div className="flex items-center justify-between gap-2">
              <p className="flex items-center gap-1 text-2xs font-semibold uppercase tracking-wide text-text-subtle">
                <MessageSquareText className="size-3" aria-hidden />
                {history.note.title}
              </p>
              <span className="shrink-0 text-2xs text-text-subtle">{relativeTime(history.note.at)}</span>
            </div>
            <p
              className={cn("mt-1 break-words text-xs text-text", compact ? "line-clamp-2" : "line-clamp-4")}
              title={history.note.body}
            >
              {history.note.body}
            </p>
          </div>
        )}

        {locked && <p className="mt-1 text-xs text-text-subtle">{history.deleted ? DELETED_NOTE : NOT_YOURS}</p>}
        {footer}
      </div>
    </li>
  );
}
